import React, { useEffect, useState, useCallback } from "react";
import { ShieldAlert, Ban, Plus, Trash2, Check, X, Loader2, Gavel, Tag, Flag, ShieldQuestion } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Spinner, EmptyState } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

export default function LicenceProtection() {
  const [block, setBlock] = useState(null);
  const [queue, setQueue] = useState(null);
  const [reports, setReports] = useState(null);
  const [appeals, setAppeals] = useState(null);
  const [term, setTerm] = useState("");
  const [busy, setBusy] = useState(false);

  const loadBlock = useCallback(() => api.get("/rights/blocklist").then((r) => setBlock(r.data)).catch(() => setBlock({ built_in: [], custom: [] })), []);
  const loadQueue = useCallback(() => api.get("/rights/review-queue").then((r) => setQueue(r.data.items)).catch(() => setQueue([])), []);
  const loadReports = useCallback(() => api.get("/rights/reports").then((r) => setReports(r.data.items)).catch(() => setReports([])), []);
  const loadAppeals = useCallback(() => api.get("/rights/appeals").then((r) => setAppeals(r.data.items)).catch(() => setAppeals([])), []);
  useEffect(() => { loadBlock(); loadQueue(); loadReports(); loadAppeals(); }, [loadBlock, loadQueue, loadReports, loadAppeals]);

  const addTerm = async () => {
    if (term.trim().length < 2) return;
    setBusy(true);
    try { await api.post("/rights/blocklist", { term: term.trim(), kind: "brand" }); setTerm(""); loadBlock(); toast.success("Added to blocklist."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const removeTerm = async (id) => { try { await api.delete(`/rights/blocklist/${id}`); loadBlock(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const review = async (id, action) => {
    try {
      await api.post(`/rights/review-queue/${id}/${action}`, action === "reject" ? { reason: "Possible protected/branded content." } : {});
      toast.success(action === "approve" ? "Listing approved & published." : "Listing rejected.");
      loadQueue();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const resolveReport = async (r, action) => {
    try {
      await api.post(`/rights/reports/${r.target_type}/${r.target_id}/resolve`, { action });
      toast.success(action === "takedown" ? `“${r.title}” was taken down.` : "Reports dismissed.");
      loadReports();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const resolveAppeal = async (a, action) => {
    try {
      await api.post(`/rights/appeals/${a.target_type}/${a.target_id}/resolve`, { action });
      toast.success(action === "restore" ? `“${a.title}” restored.` : "Appeal declined — stays removed.");
      loadAppeals();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!block || !queue) return <Spinner />;

  return (
    <div className="space-y-6" data-testid="licence-protection">
      <div className="rounded-2xl border border-[hsl(var(--primary))]/25 bg-[hsl(var(--primary))]/5 p-4 flex items-start gap-3 text-sm">
        <ShieldAlert className="w-5 h-5 text-[hsl(var(--primary))] shrink-0 mt-0.5" />
        <div><b>Licence & IP protection.</b> Listings mentioning a built-in or custom blocked term are held for review instead of going live. Game titles and marketplace items are scanned automatically. Players can also report content for your team to action below.</div>
      </div>

      {/* Community reports */}
      <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5">
        <h3 className="font-display font-bold flex items-center gap-2 mb-3"><Flag className="w-5 h-5 text-red-500" /> Reported content <Badge variant="secondary" data-testid="reports-count">{reports?.length || 0}</Badge></h3>
        {reports === null ? <Spinner /> : reports.length === 0 ? (
          <EmptyState emoji="🛡️" title="Nothing reported" text="Player-submitted reports of templates and listings will appear here." />
        ) : (
          <div className="space-y-2" data-testid="reports-list">
            {reports.map((r) => (
              <div key={`${r.target_type}-${r.target_id}`} className="rounded-xl border border-[hsl(var(--border))] p-3" data-testid={`report-item-${r.target_id}`}>
                <div className="flex items-center gap-3">
                  <Badge variant="outline" className="text-[10px] capitalize shrink-0">{r.target_type}</Badge>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold truncate">{r.title}</div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))] truncate">{r.count} report{r.count === 1 ? "" : "s"} · {Object.entries(r.reasons).map(([k, v]) => `${k} (${v})`).join(", ")}</div>
                  </div>
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => resolveReport(r, "dismiss")} data-testid={`report-dismiss-${r.target_id}`}><Check className="w-4 h-4" /> Dismiss</Button>
                  <Button size="sm" className="rounded-full bg-red-600 hover:bg-red-500" onClick={() => resolveReport(r, "takedown")} data-testid={`report-takedown-${r.target_id}`}><X className="w-4 h-4" /> Take down</Button>
                </div>
                {r.samples?.some((s) => s.details) && (
                  <div className="mt-2 pl-1 space-y-0.5">
                    {r.samples.filter((s) => s.details).map((s, i) => (
                      <div key={i} className="text-xs text-[hsl(var(--muted-foreground))]">“{s.details}” — {s.by}</div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Appeals */}
      {appeals?.length > 0 && (
        <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-5" data-testid="appeals-section">
          <h3 className="font-display font-bold flex items-center gap-2 mb-3"><ShieldQuestion className="w-5 h-5 text-amber-500" /> Takedown appeals <Badge variant="secondary" data-testid="appeals-count">{appeals.length}</Badge></h3>
          <div className="space-y-2" data-testid="appeals-list">
            {appeals.map((a) => (
              <div key={`${a.target_type}-${a.target_id}`} className="rounded-xl border border-[hsl(var(--border))] p-3" data-testid={`appeal-item-${a.target_id}`}>
                <div className="flex items-center gap-3">
                  <Badge variant="outline" className="text-[10px] capitalize shrink-0">{a.target_type}</Badge>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold truncate">{a.title}</div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))] truncate">by {a.owner_name || "creator"}</div>
                  </div>
                  <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-500" onClick={() => resolveAppeal(a, "restore")} data-testid={`appeal-restore-${a.target_id}`}><Check className="w-4 h-4" /> Restore</Button>
                  <Button size="sm" variant="outline" className="rounded-full text-red-500" onClick={() => resolveAppeal(a, "reject")} data-testid={`appeal-reject-${a.target_id}`}><X className="w-4 h-4" /> Keep removed</Button>
                </div>
                {a.appeal?.message && <div className="mt-2 text-xs text-[hsl(var(--muted-foreground))]">“{a.appeal.message}”</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Review queue */}
      <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5">
        <h3 className="font-display font-bold flex items-center gap-2 mb-3"><Gavel className="w-5 h-5 text-amber-500" /> Marketplace review queue <Badge variant="secondary" data-testid="queue-count">{queue.length}</Badge></h3>
        {queue.length === 0 ? (
          <EmptyState emoji="✅" title="Queue is clear" text="No listings are waiting for review." />
        ) : (
          <div className="space-y-2" data-testid="review-queue">
            {queue.map((l) => (
              <div key={l.id} className="flex items-center gap-3 rounded-xl border border-[hsl(var(--border))] p-3" data-testid={`queue-item-${l.id}`}>
                <span className="text-2xl">{l.preview_emoji}</span>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold truncate">{l.title}</div>
                  <div className="text-xs text-[hsl(var(--muted-foreground))] truncate">by {l.seller_name} · {l.flag_reason}</div>
                </div>
                <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-500" onClick={() => review(l.id, "approve")} data-testid={`approve-${l.id}`}><Check className="w-4 h-4" /> Approve</Button>
                <Button size="sm" variant="outline" className="rounded-full text-red-500" onClick={() => review(l.id, "reject")} data-testid={`reject-${l.id}`}><X className="w-4 h-4" /> Reject</Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Blocklist */}
      <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5">
        <h3 className="font-display font-bold flex items-center gap-2 mb-3"><Ban className="w-5 h-5 text-red-500" /> Banned brands & keywords</h3>
        <div className="flex gap-2 mb-4 max-w-md">
          <Input value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Add a brand or keyword to block…" onKeyDown={(e) => e.key === "Enter" && addTerm()} data-testid="blocklist-input" />
          <Button onClick={addTerm} disabled={busy} className="rounded-full shrink-0" data-testid="blocklist-add">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add</Button>
        </div>
        {block.custom.length > 0 && (
          <div className="mb-4">
            <div className="text-xs font-semibold text-[hsl(var(--muted-foreground))] mb-2">Custom ({block.custom.length})</div>
            <div className="flex flex-wrap gap-1.5" data-testid="custom-blocklist">
              {block.custom.map((b) => (
                <Badge key={b.id} variant="secondary" className="gap-1 pr-1 capitalize">{b.term}
                  <button onClick={() => removeTerm(b.id)} data-testid={`remove-block-${b.id}`}><Trash2 className="w-3 h-3" /></button>
                </Badge>
              ))}
            </div>
          </div>
        )}
        <div className="text-xs font-semibold text-[hsl(var(--muted-foreground))] mb-2 flex items-center gap-1.5"><Tag className="w-3.5 h-3.5" /> Built-in protected list ({block.built_in.length})</div>
        <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto">
          {block.built_in.map((t) => <Badge key={t} variant="outline" className="text-[11px] capitalize opacity-70">{t}</Badge>)}
        </div>
      </div>
    </div>
  );
}
